import React, { useState } from 'react';
import { FaUpload, FaLink, FaDownload, FaSpinner } from 'react-icons/fa';

export default function ArtModal({ isOpen, onClose, cardParams, setCardParams }) {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    setError('');
    const file = e.target.files[0];
    if (!file) return;

    // Optional: Fail early if it's obviously not an image
    if (!file.type.startsWith('image/')) {
      setError('Selected file is not an image.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setCardParams(prev => ({
        ...prev,
        Art: { File: event.target.result, originalUrl: '', x: null, y: null, scale: null }
      }));
      onClose();
    };
    reader.readAsDataURL(file);
  };

  const handleUrlLoad = async () => {
    if (!url) return;
    setIsLoading(true);
    setError('');
    try {
      const proxyUrl = `/api/image-proxy.php?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        throw new Error("Network response was not ok. The image might be blocked or missing.");
      }
      
      // 1. Check Content-Type (catches Imgur gallery links, HTML pages, etc.)
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.startsWith('image/')) {
        throw new Error("URL does not point to a valid image format. Make sure it's a direct image link.");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      // 2. Secretly test if the browser can actually decode it as an image (Mimicking the legacy code)
      await new Promise((resolve, reject) => {
        const tempImg = new Image();
        tempImg.onload = resolve; // Image is valid and decoded
        tempImg.onerror = () => {
          // Cleanup the bad object URL so it doesn't leak memory
          URL.revokeObjectURL(objectUrl);
          reject(new Error("File loaded but is corrupted or not a valid image format."));
        };
        tempImg.src = objectUrl;
      });
      
      // If we reach here, it's 100% a valid image
      setCardParams(prev => ({
        ...prev,
        Art: { File: objectUrl, originalUrl: url, x: null, y: null, scale: null }
      }));
      onClose();
    } catch (err) {
      setError(err.message || "Failed to load image. Make sure the URL is a direct image link.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content text-bg-dark border-secondary">
          <div className="modal-header border-secondary">
            <h5 className="modal-title">Select Card Art</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            
            {/* File Option (Top) */}
            <div className="mb-4">
              <label className="form-label d-flex align-items-center">
                <FaUpload className="me-2" /> Local File
              </label>
              <div className="text-center p-4 border border-secondary rounded" style={{ borderStyle: 'dashed' }}>
                <input 
                  type="file" 
                  className="form-control bg-dark text-light border-secondary" 
                  accept="image/*" 
                  onChange={handleFileChange} 
                />
              </div>
            </div>

            <div className="text-center my-3 text-muted">— OR —</div>

            {/* URL Option (Bottom) */}
            <div>
              <label className="form-label d-flex align-items-center">
                <FaLink className="me-2" /> Image URL (Imgur, Discord, etc.)
              </label>
              <div className="input-group">
                <input 
                  type="text" 
                  className="form-control bg-dark text-light border-secondary" 
                  placeholder="https://..." 
                  value={url} 
                  onChange={(e) => {
                    setUrl(e.target.value);
                    if (error) setError('');
                  }} 
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleUrlLoad();
                  }}
                />
                <button 
                  className="btn btn-primary" 
                  onClick={handleUrlLoad} 
                  disabled={isLoading || !url}
                >
                  {isLoading ? <FaSpinner className="fa-spin" /> : <><FaDownload className="me-1"/> Load</>}
                </button>
              </div>

              {/* Loader */}
              {isLoading && (
                <div className="progress mt-3 border border-secondary bg-dark" style={{ height: '8px' }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated w-100 bg-success"></div>
                </div>
              )}

              {/* Error Message Display */}
              {error && (
                <div className="text-danger small fw-bold mt-2">
                  {error}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}